import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Button } from '~/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '~/components/ui/dialog'
import { FormInput } from '~/components/ui/form-items/input'
import { FormSwitch } from '~/components/ui/form-items/switch'
import { useMutation } from '~/hooks/use-mutation'
import { useQueryClientHelper } from '~/hooks/use-query-client'
import { useToast } from '~/hooks/use-toast'
import { ngWordSchema, type NgWordFormData } from '~/schemas/ng-word'
import { useAppStore } from '~/stores/app'
import { DialogId } from '~/types/common'
import { MutationKey } from '~/types/mutation-key'
import type { NgWord } from '~/types/ng-word'
import { QueryKey } from '~/types/query-key'

type NgWordFormDialogProps = {
  editingItem?: NgWord | null
}

export function NgWordFormDialog({ editingItem }: NgWordFormDialogProps) {
  const { t } = useTranslation()
  const { showSuccess } = useToast()
  const { invalidateQueries } = useQueryClientHelper()
  const openDialogIds = useAppStore((state) => state.openDialogIds)
  const closeDialog = useAppStore((state) => state.closeDialog)

  const isCreateOpen = openDialogIds.includes(DialogId.CREATE_NG_WORD)
  const isUpdateOpen = openDialogIds.includes(DialogId.UPDATE_NG_WORD)
  const isOpen = isCreateOpen || isUpdateOpen
  const isEditing = isUpdateOpen && !!editingItem

  const dialogId = isEditing ? DialogId.UPDATE_NG_WORD : DialogId.CREATE_NG_WORD

  const { control, handleSubmit, reset } = useForm<NgWordFormData>({
    resolver: zodResolver(ngWordSchema),
    defaultValues: {
      word: '',
      isActive: true,
    },
  })

  useEffect(() => {
    if (isEditing && editingItem) {
      reset({ word: editingItem.word, isActive: editingItem.isActive })
    } else if (isCreateOpen) {
      reset({ word: '', isActive: true })
    }
  }, [isEditing, isCreateOpen, editingItem, reset])

  const { mutate: createNgWord, isPending: isCreating } = useMutation<NgWord, NgWordFormData>({
    mutationKey: MutationKey.CREATE_NG_WORD,
    method: 'POST',
    options: {
      onSuccess: () => {
        showSuccess(t('common.save'))
        invalidateQueries(QueryKey.NG_WORDS)
        closeDialog(DialogId.CREATE_NG_WORD)
      },
    },
  })

  const { mutate: updateNgWord, isPending: isUpdating } = useMutation<NgWord, NgWordFormData>({
    mutationKey: MutationKey.UPDATE_NG_WORD,
    method: 'PUT',
    idParams: editingItem ? { id: editingItem.id } : undefined,
    options: {
      onSuccess: () => {
        showSuccess(t('common.save'))
        invalidateQueries(QueryKey.NG_WORDS)
        closeDialog(DialogId.UPDATE_NG_WORD)
      },
    },
  })

  const isPending = isCreating || isUpdating

  const onSubmit = (data: NgWordFormData) => {
    if (isEditing) {
      updateNgWord(data)
    } else {
      createNgWord(data)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeDialog(dialogId)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEditing ? t('ngWords.editWord') : t('ngWords.addNew')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <FormInput control={control} name="word" label={t('ngWords.word')} required />
          <FormSwitch control={control} name="isActive" label={t('common.active')} />
          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => closeDialog(dialogId)} disabled={isPending}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={isPending}>
              {t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
